import { createSelector } from '@reduxjs/toolkit';

import { hasNewLayoutModeFeatureFlag, hasProjectHierarchyFeatureFlag } from '@paragon/login-tools';

import { getSelectedItemIdentifiers } from '../../library/selection/selectors';
import { isConsideredLayoutItem } from '../../library/selection/types';
import { RootState } from '../index';
import { getCurrentCameraType, getCurrentRenderMode } from '../slices/camera/selectors';
import { getIsLayoutHierarchyPresentInProject } from './layoutDataSelectors';

export const is2DLayoutCameraInUse = createSelector(getCurrentCameraType, (camera) => camera === 'orthographic-layout');
export const is3DLayoutCameraInUse = createSelector(getCurrentCameraType, (camera) => camera === 'perspective-layout');

export const isLayoutCameraInUse = createSelector(getCurrentRenderMode, (renderMode) => renderMode === 'layout');

export const anyLayoutItemsExist = createSelector(
  (state: RootState) => Object.keys(state.layout.roofContainers).length > 0,
  (state: RootState) => Object.keys(state.layout.roofPlanes).length > 0,
  (state: RootState) => Object.keys(state.layout.featurePatternContainers).length > 0,
  (state: RootState) => Object.keys(state.layout.bearingEnvelopes).length > 0,

  (roofContainersExist, roofPlanesExist, featurePatternContainersExist, bearingEnvelopesExist) =>
    roofContainersExist || roofPlanesExist || featurePatternContainersExist || bearingEnvelopesExist,
);

export const isLayoutModeEnabled = createSelector(
  hasNewLayoutModeFeatureFlag,
  (state: RootState) => state.application.openContext,
  anyLayoutItemsExist,

  (hasFeatureFlag, context, anyLayoutItemsExist) =>
    (hasFeatureFlag && context?.type === 'project') || anyLayoutItemsExist,
);

export const isLayoutViewActive = createSelector(
  isLayoutModeEnabled,
  isLayoutCameraInUse,
  getSelectedItemIdentifiers,
  (modeEnabled, layoutCameraInUse, selectedItems) => {
    if (modeEnabled === false || selectedItems.length === 0) {
      return false;
    }

    return layoutCameraInUse && selectedItems.every((selectedItem) => isConsideredLayoutItem(selectedItem.type));
  },
);

export const isLayoutModeViewOnly = createSelector(
  hasNewLayoutModeFeatureFlag,

  (hasFeatureFlag) => !hasFeatureFlag,
);

export const getLayoutHierarchyAccessLevel = createSelector(
  isLayoutModeEnabled,
  hasProjectHierarchyFeatureFlag,
  getIsLayoutHierarchyPresentInProject,
  (layoutModeEnabled, hasProjectHierarchyFeatureFlag, layoutHasHierarchy): { read: boolean; write: boolean } => {
    if (!layoutModeEnabled) return { read: false, write: false };

    return {
      read: layoutHasHierarchy || hasProjectHierarchyFeatureFlag,
      write: hasProjectHierarchyFeatureFlag,
    };
  },
);

// This selector is the source of truth for whether the current layout is based on roof planes. Accept no substitutes!
export const isLayoutBasedOnRoofPlanes = (state: RootState) => Object.keys(state.layout.roofContainers).length === 0;

// This selector is the source of truth for whether the current layout is based on roof containers. Accept no substitutes!
export const isLayoutBasedOnRoofContainers = (state: RootState) => Object.keys(state.layout.roofContainers).length > 0;

// for internal use by `shouldShowBearingEnvelopeDimensions` -- return a boolean to force visibility, or `undefined` to
//   respect the user's configuration
const getForcedBearingEnvelopeDimensionVisibility = createSelector(
  (state: RootState) => state.application.insertingObjectInfo?.type === 'bearingEnvelope',
  (insertingBearingEnvelope) => {
    if (insertingBearingEnvelope) return true;

    return undefined;
  },
);

export const shouldShowBearingEnvelopeDimensions = createSelector(
  (state: RootState) => state.visualOptions.showBearingDimensionsInLayout,
  getForcedBearingEnvelopeDimensionVisibility,

  (settingValue, forcedOverride) => ({
    visible: forcedOverride ?? settingValue,

    setting: settingValue,
    overridden: forcedOverride !== undefined,
  }),
);
